import { QueuedIntakeFileInfo, UploadData, UploadStoreState, useUploadStore } from '../../store/uploadStore';
import { validateFile } from './validate/validateFile';
import { UploadType } from '../../consts/uploadType';
import { hasHitParallelValidationLimit } from './validate/ValidationWorkerManager';
import { UploadNotRestoredError } from '../../errors/UploadNotRestoredError';
import { releaseWakeLock, setupWakeLock } from './wakeLock';
import { isCancelledByUserError } from '../../errors/createCanceledError';
import { hasHitParallelUploadLimit, uploadFile } from './upload/uploadFile';
import './preventLeaving';

const shouldSkipValidation = (file: QueuedIntakeFileInfo) => {
  return !!file.validation || file.isBeingValidated;
};

const shouldSkipUpload = (file: QueuedIntakeFileInfo) => {
  return !file.validation || file.isBeingUploaded;
};

export class IntakeLoop {
  runLoop = () => {
    const uploads = useUploadStore.getState().uploads;
    const getGeoref = useUploadStore.getState().getGeoref;

    if (this.hasJobs(uploads)) {
      setupWakeLock();
    } else {
      releaseWakeLock();
    }

    this.process(uploads, getGeoref);
  };

  private process = (uploads: UploadStoreState['uploads'], getGeoref: UploadStoreState['getGeoref']) => {
    this.processValidation(uploads, getGeoref);
    this.processUpload(uploads);
  };

  private processValidation = (uploads: UploadStoreState['uploads'], getGeoref: UploadStoreState['getGeoref']) => {
    // Run validation. Note that were are mutating the object itself inside the validator.
    outerLoop: for (const upload of Object.values(uploads)) {
      for (const file of upload.queue) {
        if (hasHitParallelValidationLimit()) {
          break outerLoop;
        }

        if (shouldSkipValidation(file)) {
          continue;
        }

        if (!upload.upload) throw new UploadNotRestoredError();
        const georef = getGeoref(upload.upload.customer, upload.upload.site, upload.upload.flightScheduleId);
        validateFile(file, upload, georef)
          .then(() => this.forceUploadUpdate(upload))
          .catch((e: unknown) => {
            if (isCancelledByUserError(e)) return null;
            throw e;
          })
          .finally(this.runLoop);
      }
    }
  };

  private processUpload = (uploads: UploadStoreState['uploads']) => {
    // Run upload. Note that were are mutating the object itself inside the validator.
    outerLoop: for (const [, upload] of Object.entries(uploads)) {
      for (const file of upload.queue) {
        if (hasHitParallelUploadLimit()) {
          break outerLoop;
        }

        if (shouldSkipUpload(file)) {
          continue;
        }

        const updateUpload = () => this.forceUploadUpdate(upload);
        uploadFile(upload, file, updateUpload)
          .then(() => this.forceUploadUpdate(upload))
          .catch((e: unknown) => {
            if (isCancelledByUserError(e)) return null;
            throw e;
          })
          .finally(this.runLoop);
      }
    }
  };

  private hasJobs = (uploads: UploadStoreState['uploads']) => {
    for (const upload of Object.values(uploads)) {
      if (upload.queue.length) return true;
    }

    return false;
  };

  private forceUploadUpdate = (upload: UploadData) => {
    // Make sure upload gets a new identity and any memoization using it gets reset
    useUploadStore
      .getState()
      .setUpload(
        upload.meta.type === UploadType.Pano,
        upload.upload!.customer,
        upload.upload!.site,
        upload.overview.flightScheduleId,
        { ...upload },
      );
  };
}

export const intakeLoop = new IntakeLoop();
